import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth';

/**
 * K4.1 (round-5) — global ⌘K / Ctrl-K command palette.
 *
 * Zero runtime deps beyond TanStack Router (already in the bundle).
 * The fuzzy scorer is a tight prefix + contains match against the
 * command label, keywords, and section — ~30 commands is small
 * enough that we don't need a real fuzzy library. Keeps the bundle
 * budget lean.
 *
 * Opens with ⌘K (macOS) / Ctrl-K (Windows/Linux), closes on Esc or
 * click-outside.
 */

type CommandKind = 'go' | 'action' | 'external';

interface Command {
  id: string;
  label: string;
  section: string;
  kind: CommandKind;
  keywords?: string[];
  run: () => void;
  shortcut?: string;
}

export function CommandPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);

  // Listen globally for ⌘K / Ctrl-K. We attach on window so it works
  // regardless of focus; inputs inherit via event propagation.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const isMetaK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isMetaK) {
        e.preventDefault();
        setOpen((prev) => !prev);
        setQuery('');
        setActiveIndex(0);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      // Defer focus to after the portal node is laid out.
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'go-home',
        label: 'Go to Home',
        section: 'Navigation',
        kind: 'go',
        keywords: ['home', 'index'],
        run: () => void navigate({ to: '/' }),
      },
      {
        id: 'go-dashboard',
        label: 'Go to Command Center',
        section: 'Navigation',
        kind: 'go',
        keywords: ['dashboard', 'cc'],
        run: () => void navigate({ to: '/dashboard' }),
      },
      {
        id: 'go-lifecycle',
        label: 'Go to Lifecycle',
        section: 'Navigation',
        kind: 'go',
        keywords: ['pipeline', 'dag', 'phases'],
        run: () => void navigate({ to: '/dashboard/lifecycle' }),
      },
      {
        id: 'go-artifacts',
        label: 'Go to Artifacts',
        section: 'Navigation',
        kind: 'go',
        keywords: ['files', 'generated'],
        run: () => void navigate({ to: '/dashboard/artifacts' }),
      },
      {
        id: 'go-dependencies',
        label: 'Go to Dependencies',
        section: 'Navigation',
        kind: 'go',
        keywords: ['deps', 'packages'],
        run: () => void navigate({ to: '/dashboard/dependencies' }),
      },
      // glistening-zebra Phase K5 — Cmd-K reachability for the two
      // routes added in F1 so users don't have to scroll the
      // sidebar to find them.
      {
        id: 'go-impact',
        label: 'Go to Impact',
        section: 'Navigation',
        kind: 'go',
        keywords: ['blast', 'radius', 'axon', 'graph', 'impact'],
        run: () => void navigate({ to: '/dashboard/impact' }),
      },
      {
        id: 'go-planning-chat',
        label: 'Go to Planning chat',
        section: 'Navigation',
        kind: 'go',
        keywords: ['plan', 'agent', 'invoke', 'prompt'],
        run: () => void navigate({ to: '/dashboard/planning-chat' }),
      },
      {
        id: 'go-activity',
        label: 'Go to Activity',
        section: 'Navigation',
        kind: 'go',
        keywords: ['agents', 'executions', 'stream'],
        run: () => void navigate({ to: '/dashboard/activity' }),
      },
      {
        id: 'go-approvals',
        label: 'Go to Approvals',
        section: 'Navigation',
        kind: 'go',
        keywords: ['review', 'decision', 'queue'],
        run: () => void navigate({ to: '/dashboard/approvals' }),
      },
      {
        id: 'go-deploy',
        label: 'Go to Deploy',
        section: 'Navigation',
        kind: 'go',
        keywords: ['helm', 'docker', 'release'],
        run: () => void navigate({ to: '/dashboard/deploy' }),
      },
      {
        id: 'go-audit',
        label: 'Go to Audit trail',
        section: 'Navigation',
        kind: 'go',
        keywords: ['merkle', 'evidence', 'compliance', 'soc2'],
        run: () => void navigate({ to: '/dashboard/audit' }),
      },
      {
        id: 'go-costs',
        label: 'Go to Costs',
        section: 'Navigation',
        kind: 'go',
        keywords: ['billing', 'byok', 'spend', 'usage'],
        run: () => void navigate({ to: '/dashboard/costs' }),
      },
      {
        id: 'go-settings',
        label: 'Go to Settings',
        section: 'Navigation',
        kind: 'go',
        keywords: ['providers', 'api keys', 'vcs'],
        run: () => void navigate({ to: '/dashboard/settings' }),
      },
      {
        id: 'go-onboarding',
        label: 'Start onboarding wizard',
        section: 'Actions',
        kind: 'action',
        keywords: ['welcome', 'setup', 'first run'],
        run: () => void navigate({ to: '/onboarding' }),
      },
      {
        id: 'act-connect-provider',
        label: 'Connect LLM provider',
        section: 'Actions',
        kind: 'action',
        keywords: ['llm', 'byok', 'anthropic', 'openai', 'google'],
        run: () => void navigate({ to: '/dashboard/settings' }),
      },
      {
        id: 'act-push-github',
        label: 'Push to GitHub / Open PR',
        section: 'Actions',
        kind: 'action',
        keywords: ['git', 'vcs', 'pull request'],
        run: () => void navigate({ to: '/dashboard/artifacts' }),
      },
      {
        id: 'act-signout',
        label: 'Sign out',
        section: 'Actions',
        kind: 'action',
        keywords: ['logout', 'exit'],
        run: () => logout(),
      },
      {
        id: 'ext-docs',
        label: 'Open docs',
        section: 'Help',
        kind: 'external',
        keywords: ['documentation', 'api reference'],
        run: () => window.open('https://sdlc.sh/docs', '_blank', 'noopener'),
      },
    ],
    [navigate, logout],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands
      .map((c) => ({ cmd: c, score: score(c, q) }))
      .filter(({ score: s }) => s > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ cmd }) => cmd);
  }, [commands, query]);

  // Clamp the active index whenever the filtered list changes.
  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  if (!open) return null;

  function close(): void {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }

  function runActive(): void {
    const cmd = filtered[activeIndex];
    if (!cmd) return;
    cmd.run();
    close();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="palette-title"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="shakti-card mt-20 w-full max-w-xl border-border-strong bg-bg-surface p-0 shadow-elev-2">
        <h2 id="palette-title" className="sr-only">
          Command palette
        </h2>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              runActive();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              close();
            }
          }}
          placeholder="Type a command or search…"
          aria-label="Command search"
          className="w-full rounded-t-md border-b border-border-subtle bg-transparent px-4 py-3 text-sm text-fg-primary outline-none"
        />
        <ul role="listbox" aria-label="Commands" className="max-h-80 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-fg-muted">No commands match.</li>
          ) : (
            filtered.map((cmd, i) => (
              <li key={cmd.id} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveIndex(i);
                    cmd.run();
                    close();
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2 text-left text-sm transition-colors ${
                    i === activeIndex
                      ? 'bg-accent-muted text-fg-primary'
                      : 'text-fg-secondary hover:bg-bg-raised hover:text-fg-primary'
                  }`}
                >
                  <span className="flex flex-col">
                    <span>{cmd.label}</span>
                    <span className="text-2xs uppercase tracking-wider text-fg-muted">
                      {cmd.section} · {cmd.kind}
                    </span>
                  </span>
                  {cmd.shortcut ? (
                    <span className="shakti-mono text-2xs text-fg-muted">{cmd.shortcut}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
        <footer className="flex items-center justify-between border-t border-border-subtle px-3 py-2 text-2xs text-fg-muted">
          <span>
            <kbd className="shakti-mono">↑↓</kbd> navigate ·{' '}
            <kbd className="shakti-mono">↵</kbd> run · <kbd className="shakti-mono">Esc</kbd>{' '}
            close
          </span>
          <span className="shakti-mono">⌘K</span>
        </footer>
      </div>
    </div>
  );
}

function score(cmd: Command, query: string): number {
  const haystack = [cmd.label, cmd.section, ...(cmd.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  // Prefix match in the label: strongest.
  if (cmd.label.toLowerCase().startsWith(query)) return 100;
  // Whole-word contains in the label.
  if (haystack.includes(` ${query}`)) return 60;
  // Substring contains anywhere.
  if (haystack.includes(query)) return 40;
  // Subsequence (each char in order) — weakest but forgiving.
  let idx = 0;
  for (const ch of haystack) {
    if (ch === query[idx]) idx += 1;
    if (idx >= query.length) return 10;
  }
  return 0;
}
